import { memo } from 'react'
import { innerRectOf, resolveFrameLayout, type Frame } from '@praesto/shared'

/**
 * Satzspiegel und Raster eines Frames, blass eingeblendet, solange darin etwas
 * verschoben oder skaliert wird.
 *
 * Ohne diese Einblendung wäre das Raster eine unsichtbare Kraft: Etwas rastet
 * ein, und niemand weiß, woran. Deshalb erscheint es genau dann, wenn es wirkt,
 * und verschwindet danach wieder — ein dauerhaft sichtbares Raster wäre auf
 * einem Board, das am Ende gezeigt wird, nur Störung.
 */
export const FrameGrid = memo(function FrameGrid({ frame }: { frame: Frame }): React.ReactElement {
  const layout = resolveFrameLayout(frame)
  const inner = innerRectOf(frame, layout.padding)

  const columns = Array.from({ length: Math.max(0, layout.columns - 1) }, (_, i) =>
    inner.x + (inner.w * (i + 1)) / layout.columns,
  )
  const rows = Array.from({ length: Math.max(0, layout.rows - 1) }, (_, i) =>
    inner.y + (inner.h * (i + 1)) / layout.rows,
  )

  return (
    <div
      className="frame-grid"
      aria-hidden="true"
      style={{
        left: `${frame.x}px`,
        top: `${frame.y}px`,
        width: `${frame.w}px`,
        height: `${frame.h}px`,
      }}
    >
      {(inner.w !== frame.w || inner.h !== frame.h) && (
        <div
          className="frame-grid-inner"
          style={{
            left: `${inner.x - frame.x}px`,
            top: `${inner.y - frame.y}px`,
            width: `${inner.w}px`,
            height: `${inner.h}px`,
          }}
        />
      )}
      {columns.map((x) => (
        <div
          key={`c${x}`}
          className="frame-grid-line is-vertical"
          style={{ left: `${x - frame.x}px`, top: `${inner.y - frame.y}px`, height: `${inner.h}px` }}
        />
      ))}
      {rows.map((y) => (
        <div
          key={`r${y}`}
          className="frame-grid-line is-horizontal"
          style={{ top: `${y - frame.y}px`, left: `${inner.x - frame.x}px`, width: `${inner.w}px` }}
        />
      ))}
    </div>
  )
})
